'use client';

import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cn } from '@/hen-ui/utils';
import {
  toggleGroupItemVariants,
  toggleGroupRootVariants,
  type ToggleGroupItemVariantProps,
  type ToggleGroupRootVariantProps,
} from './toggle-group.variants';

type ToggleGroupSize = NonNullable<ToggleGroupItemVariantProps['size']>;
type ToggleGroupVariant = NonNullable<ToggleGroupItemVariantProps['variant']>;

type ToggleGroupStyleContextValue = {
  size: ToggleGroupSize;
  variant: ToggleGroupVariant;
  /** True when a `maxSelection` cap has been hit (multiple mode only). */
  isMaxReached?: boolean;
  /** Currently selected values, used to keep selected items togglable at the cap. */
  selectedValues?: readonly string[];
};

const ToggleGroupStyleContext =
  React.createContext<ToggleGroupStyleContextValue | null>(null);

const DEFAULT_SIZE: ToggleGroupSize = 'sm';
const DEFAULT_VARIANT: ToggleGroupVariant = 'grayOutline';

type ToggleGroupCommonProps = ToggleGroupRootVariantProps & {
  name?: string;
  size?: ToggleGroupSize;
  variant?: ToggleGroupVariant;
  /**
   * Maximum number of items selectable at once. Only enforced in
   * `type="multiple"` mode. When the cap is reached, further selections are
   * ignored and unselected items are disabled (already-selected items stay
   * togglable so the user can swap). Omit, or pass `0`/a negative value, for
   * no limit.
   */
  maxSelection?: number;
};

type ToggleGroupSingleProps = ToggleGroupCommonProps &
  Omit<
    React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>,
    'type' | 'value' | 'defaultValue' | 'onValueChange'
  > & {
    type?: 'single';
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
  };

type ToggleGroupMultipleProps = ToggleGroupCommonProps &
  Omit<
    React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>,
    'type' | 'value' | 'defaultValue' | 'onValueChange'
  > & {
    type: 'multiple';
    value?: string[];
    defaultValue?: string[];
    onValueChange?: (value: string[]) => void;
  };

export type ToggleGroupProps =
  | ToggleGroupSingleProps
  | ToggleGroupMultipleProps;

export type ToggleGroupItemProps = React.ComponentPropsWithoutRef<
  typeof ToggleGroupPrimitive.Item
> &
  ToggleGroupItemVariantProps;

const getSingleValue = (
  isControlled: boolean,
  controlledValue: string | undefined,
  internalValue: string,
): string => {
  return isControlled ? (controlledValue ?? '') : internalValue;
};

const getMultipleValue = (
  isControlled: boolean,
  controlledValue: string[] | undefined,
  internalValue: string[],
): string[] => {
  return isControlled ? (controlledValue ?? []) : internalValue;
};

/** Radix expects `string` for single and `string[]` for multiple; Storybook args can mismatch after toggling `type`. */
function normalizeSingleSelectionValue(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    const first = value.find((v) => typeof v === 'string' && v !== '');
    return typeof first === 'string' ? first : '';
  }
  return '';
}

function normalizeMultipleSelectionValue(value: unknown): string[] {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  if (typeof value === 'string') {
    return value === '' ? [] : [value];
  }
  return [];
}

/**
 * Clamp a multiple-selection value to the cap. Enforcing the cap only on change
 * leaves an over-cap initial/controlled value (e.g. a DSL config that supplies
 * more defaults than `maxSelection`) rendering every item selected until the
 * user deselects one. Clamping here holds the invariant regardless of consumer
 * input. A non-positive or absent cap means "no limit".
 */
function clampToMaxSelection(
  values: string[],
  maxSelection: number | undefined,
): string[] {
  if (
    typeof maxSelection === 'number' &&
    maxSelection > 0 &&
    values.length > maxSelection
  ) {
    return values.slice(0, maxSelection);
  }
  return values;
}

export const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>(
  (
    {
      className,
      name,
      wrap,
      size = DEFAULT_SIZE,
      variant = DEFAULT_VARIANT,
      type = 'single',
      maxSelection,
      ...props
    },
    ref,
  ) => {
    const hasSelectionCap =
      typeof maxSelection === 'number' && maxSelection > 0;
    const styles = React.useMemo<ToggleGroupStyleContextValue>(
      () => ({
        size,
        variant,
      }),
      [size, variant],
    );
    const singleProps = props as ToggleGroupSingleProps;
    const multipleProps = props as ToggleGroupMultipleProps;

    const isSingleControlled =
      type === 'single' && singleProps.value !== undefined;
    const isMultipleControlled =
      type === 'multiple' && multipleProps.value !== undefined;

    const onSingleValueChange = singleProps.onValueChange;
    const onMultipleValueChange = multipleProps.onValueChange;

    const [singleInternalValue, setSingleInternalValue] = React.useState(() =>
      type === 'single'
        ? normalizeSingleSelectionValue(singleProps.defaultValue)
        : '',
    );
    const [multipleInternalValue, setMultipleInternalValue] = React.useState<
      string[]
    >(() =>
      type === 'multiple'
        ? clampToMaxSelection(
            normalizeMultipleSelectionValue(multipleProps.defaultValue),
            maxSelection,
          )
        : [],
    );

    const previousTypeRef = React.useRef(type);

    React.useEffect(() => {
      if (previousTypeRef.current === type) {
        return;
      }
      previousTypeRef.current = type;
      if (type === 'single') {
        setSingleInternalValue(
          normalizeSingleSelectionValue(singleProps.defaultValue),
        );
        setMultipleInternalValue([]);
      } else {
        setMultipleInternalValue(
          clampToMaxSelection(
            normalizeMultipleSelectionValue(multipleProps.defaultValue),
            maxSelection,
          ),
        );
        setSingleInternalValue('');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- only remirror when `type` changes; defaults are read at switch time
    }, [type]);

    const handleSingleChange = React.useCallback(
      (nextValue: string) => {
        if (!isSingleControlled) {
          setSingleInternalValue(nextValue);
        }
        onSingleValueChange?.(nextValue);
      },
      [isSingleControlled, onSingleValueChange],
    );

    const handleMultipleChange = React.useCallback(
      (nextValue: string[]) => {
        // Enforce the selection cap: reject any change that would grow the
        // selection beyond `maxSelection`. Deselections (which shrink or keep
        // the count) always pass, so a user at the cap can still toggle off.
        if (
          hasSelectionCap &&
          typeof maxSelection === 'number' &&
          nextValue.length > maxSelection
        ) {
          return;
        }
        if (!isMultipleControlled) {
          setMultipleInternalValue(nextValue);
        }
        onMultipleValueChange?.(nextValue);
      },
      [
        isMultipleControlled,
        onMultipleValueChange,
        hasSelectionCap,
        maxSelection,
      ],
    );

    // Clamp the controlled value and the uncontrolled default so an over-cap
    // consumer input never renders more than `maxSelection` items selected.
    const normalizedMultipleDefault = clampToMaxSelection(
      normalizeMultipleSelectionValue(multipleProps.defaultValue),
      maxSelection,
    );
    const normalizedMultipleValue = clampToMaxSelection(
      normalizeMultipleSelectionValue(multipleProps.value),
      maxSelection,
    );

    const multipleCurrentValue = getMultipleValue(
      isMultipleControlled,
      isMultipleControlled ? normalizedMultipleValue : undefined,
      multipleInternalValue,
    );

    const multipleSelectionKey = multipleCurrentValue.join('\u0000');
    const multipleStyles = React.useMemo<ToggleGroupStyleContextValue>(
      () => ({
        ...styles,
        isMaxReached:
          hasSelectionCap &&
          typeof maxSelection === 'number' &&
          multipleCurrentValue.length >= maxSelection,
        selectedValues: multipleCurrentValue,
      }),
      // `multipleSelectionKey` is the stable signal for `multipleCurrentValue`'s
      // contents (its identity changes every render in controlled mode).
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [styles, hasSelectionCap, maxSelection, multipleSelectionKey],
    );

    if (type === 'multiple') {
      const {
        defaultValue: _multipleDefaultRaw,
        value: _multipleValueRaw,
        ...multipleRest
      } = multipleProps;
      const currentValue = multipleCurrentValue;

      return (
        <ToggleGroupStyleContext.Provider value={multipleStyles}>
          <ToggleGroupPrimitive.Root
            ref={ref}
            {...multipleRest}
            type='multiple'
            className={cn(toggleGroupRootVariants({ wrap }), className)}
            onValueChange={handleMultipleChange}
            {...(isMultipleControlled
              ? { value: normalizedMultipleValue }
              : { defaultValue: normalizedMultipleDefault })}
          />
          {name
            ? currentValue.map((itemValue) => (
                <input
                  key={itemValue}
                  type='hidden'
                  name={name}
                  value={itemValue}
                  readOnly
                  disabled={multipleRest.disabled}
                />
              ))
            : null}
        </ToggleGroupStyleContext.Provider>
      );
    }

    const {
      defaultValue: singleDefaultRaw,
      value: singleValueRaw,
      ...singleRest
    } = singleProps;

    const normalizedSingleDefault =
      normalizeSingleSelectionValue(singleDefaultRaw);
    const normalizedSingleValue = normalizeSingleSelectionValue(singleValueRaw);

    const currentValue = getSingleValue(
      isSingleControlled,
      isSingleControlled ? normalizedSingleValue : undefined,
      singleInternalValue,
    );

    return (
      <ToggleGroupStyleContext.Provider value={styles}>
        <ToggleGroupPrimitive.Root
          ref={ref}
          {...singleRest}
          type='single'
          className={cn(toggleGroupRootVariants({ wrap }), className)}
          onValueChange={handleSingleChange}
          {...(isSingleControlled
            ? { value: normalizedSingleValue }
            : { defaultValue: normalizedSingleDefault })}
        />
        {name && currentValue !== '' ? (
          <input
            type='hidden'
            name={name}
            value={currentValue}
            readOnly
            disabled={singleRest.disabled}
          />
        ) : null}
      </ToggleGroupStyleContext.Provider>
    );
  },
);

ToggleGroup.displayName = 'ToggleGroup';

export const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  ToggleGroupItemProps
>(({ className, size, variant, disabled, value, ...props }, ref) => {
  const styles = React.useContext(ToggleGroupStyleContext);
  const resolvedSize = size ?? styles?.size ?? DEFAULT_SIZE;
  const resolvedVariant = variant ?? styles?.variant ?? DEFAULT_VARIANT;

  // When a `maxSelection` cap is hit, disable any item that is NOT already
  // selected so the user cannot exceed the limit; selected items stay enabled
  // so they can be toggled off. An author-supplied `disabled` always wins.
  const isSelected =
    typeof value === 'string' && styles?.selectedValues
      ? styles.selectedValues.includes(value)
      : false;
  const disabledByMax = Boolean(styles?.isMaxReached) && !isSelected;
  const resolvedDisabled = disabled || disabledByMax;

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      value={value}
      disabled={resolvedDisabled}
      // Make the cap-driven disabled state explicit to assistive tech rather
      // than relying on the silent native `disabled` alone.
      aria-disabled={disabledByMax || undefined}
      className={cn(
        toggleGroupItemVariants({
          size: resolvedSize,
          variant: resolvedVariant,
        }),
        className,
      )}
      {...props}
    />
  );
});

ToggleGroupItem.displayName = 'ToggleGroupItem';
